import React, { useEffect, useState } from "react";
import { Box, Button, ButtonGroup } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { useTheme } from "@mui/material/styles";
import LoadingPage from "../../common/LoadingPage";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import {
  Prompt,
  useCreatePromptDocumentMutation,
  useCreatePromptDocumentRefinementMutation,
  useCreateTranscribedTextPromptMutation,
} from "../../store/userApi";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface PrepareEmailTabProps {
  prompt: Prompt;
  handleDownload: (text: string | null, filename: string) => void;
  inputText: string;
}

const PromptTab: React.FC<PrepareEmailTabProps> = ({
  prompt,
  handleDownload,
  inputText,
}) => {
  const theme = useTheme();
  const [text, setText] = useState<string>(inputText);
  const [refinedText, setRefinedText] = useState<null | string>(null);
  const [selectedPrimaryButton, setSelectedPrimaryButton] = useState<
    string | null
  >(null);
  const [selectedSecondaryButton, setSelectedSecondaryButton] = useState<
    string | null
  >(null);

  const [
    getTranscribedTextDocument,
    { isLoading: gettingTranscribedTextDocument },
  ] = useCreateTranscribedTextPromptMutation();

  const [getDocumentPrompt, { isLoading: creatingDocumentPrompt }] =
    useCreatePromptDocumentMutation();

  const [
    getDocumentRefinementPrompt,
    { isLoading: creatingDocumentRefinementPrompt },
  ] = useCreatePromptDocumentRefinementMutation();

  /** This is the very first mutation that is called
   *
   */
  const handleInitialTranscribedTextPrompt = async () => {
    try {
      const response = await getTranscribedTextDocument({
        id: prompt.id,
        text: inputText,
      }).unwrap();
      setText(response.gptTextResponse);
    } catch (error) {
      showErrorToast(`Failed to get document ${getErrorMessage(error)}`);
    }
  };

  const getGptResponse = async (primaryPromptId: string) => {
    try {
      const response = await getDocumentPrompt({
        id: prompt.id,
        primaryPromptId,
        text: inputText,
      }).unwrap();
      setRefinedText(null);
      setText(response.gptTextResponse);
    } catch (e) {
      showErrorToast(getErrorMessage(e));
    }
  };

  const handlePrimaryButtonClick = (primaryPromptId: string) => {
    if (creatingDocumentPrompt || creatingDocumentRefinementPrompt) {
      return;
    }

    setSelectedPrimaryButton(primaryPromptId);
    setSelectedSecondaryButton(null);
    getGptResponse(primaryPromptId);
  };

  const handleSecondaryButtonClick = async (secondaryPromptId: string) => {
    if (creatingDocumentPrompt || creatingDocumentRefinementPrompt) {
      return;
    }

    setSelectedSecondaryButton(secondaryPromptId);
    try {
      const response = await getDocumentRefinementPrompt({
        text,
        secondaryPromptId,
        id: prompt.id,
      }).unwrap();
      setRefinedText(response.gptTextResponse);
    } catch (e) {
      showErrorToast(getErrorMessage(e));
    }
  };

  useEffect(() => {
    handleInitialTranscribedTextPrompt();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: theme.palette.background.default,
        padding: "16px",
      }}
    >
      {/* Button groups for primary and secondary refinements */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: "600px",
          width: "100%",
          padding: "16px",
        }}
      >
        {/* Primary button group for selecting prompts */}
        <ButtonGroup
          disabled={
            creatingDocumentPrompt ||
            creatingDocumentRefinementPrompt ||
            gettingTranscribedTextDocument
          }
          variant="outlined"
          aria-label="primary button group"
          sx={{
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {prompt.primaryPrompts.map((primaryPrompt) => (
            <Button
              key={primaryPrompt.id}
              onClick={() => handlePrimaryButtonClick(primaryPrompt.id)}
              sx={{
                borderColor: theme.palette.primary.main,
                color:
                  selectedPrimaryButton === primaryPrompt.id
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
                backgroundColor:
                  selectedPrimaryButton === primaryPrompt.id
                    ? theme.palette.primary.main
                    : "transparent",
                margin: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.common.white,
                },
              }}
            >
              {primaryPrompt.title}
            </Button>
          ))}
        </ButtonGroup>

        {/* Secondary button group for refining the prompt */}
        {selectedPrimaryButton && prompt.secondaryPrompts.length > 0 && (
          <ButtonGroup
            disabled={
              creatingDocumentPrompt ||
              creatingDocumentRefinementPrompt ||
              gettingTranscribedTextDocument
            }
            variant="outlined"
            aria-label="secondary button group"
            sx={{
              flexWrap: "wrap",
            }}
          >
            {prompt.secondaryPrompts.map((secondaryPrompt) => (
              <Button
                key={secondaryPrompt.id}
                onClick={() => handleSecondaryButtonClick(secondaryPrompt.id)}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color:
                    selectedSecondaryButton === secondaryPrompt.id
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  backgroundColor:
                    selectedSecondaryButton === secondaryPrompt.id
                      ? theme.palette.primary.main
                      : "transparent",
                  margin: "4px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.common.white,
                  },
                }}
              >
                {secondaryPrompt.title}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Box>

      {/* Text content section */}
      <Box
        sx={{
          textAlign: "left",
          width: "100%",
          maxWidth: "600px",
          padding: "16px",
          borderRadius: "12px",
        }}
      >
        {creatingDocumentPrompt ||
        creatingDocumentRefinementPrompt ||
        gettingTranscribedTextDocument ? (
          <LoadingPage />
        ) : (
          <ReactMarkdown>{refinedText ?? text}</ReactMarkdown>
        )}

        <Button
          variant="contained"
          onClick={() => {
            const contentToCopy = refinedText ?? text;
            if (contentToCopy) {
              navigator.clipboard
                .writeText(contentToCopy)
                .then(() => {
                  showSuccessToast("Copied to clipboard!");
                })
                .catch((err) => {
                  showErrorToast(
                    `Failed to copy to clipboard ${getErrorMessage(err)}`,
                  );
                });
            }
          }}
          sx={{
            mt: 4,
            mr: 4,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
          startIcon={<ContentCopyIcon />}
        >
          Copy to Clipboard
        </Button>

        <Button
          variant="contained"
          onClick={() =>
            handleDownload(refinedText ?? text, `${prompt.title}-email.pdf`)
          }
          sx={{
            mt: 4,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Download
        </Button>
      </Box>
    </Box>
  );
};

export default PromptTab;
